import React, { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { getJobs } from '../../services/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import {
  Briefcase,
  Clock,
  CheckCircle,
  Calendar,
  User,
  ChevronRight,
  Search,
  Filter
} from 'lucide-react';
import { Input } from '../../components/ui/input';
import { useLanguage } from '../../context/LanguageContext';

const JobsList = () => {
  const { t, locale } = useLanguage();
  const statusLabels = {
    planned: t('portalUi.status.planned', 'Plānots'),
    in_progress: t('portalUi.status.inProgress', 'Procesā'),
    completed: t('portalUi.status.completed', 'Pabeigts')
  };

  const statusColors = {
    planned: 'bg-blue-100 text-blue-700 border-blue-200',
    in_progress: 'bg-amber-100 text-amber-700 border-amber-200',
    completed: 'bg-green-100 text-green-700 border-green-200'
  };

  const statusIcons = {
    planned: Clock,
    in_progress: Briefcase,
    completed: CheckCircle
  };

  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');

  const fetchJobs = useCallback(async () => {
    try {
      const data = await getJobs();
      setJobs(data);
    } catch (error) {
      console.error('Failed to fetch jobs:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchJobs();
  }, [fetchJobs]);

  const filteredJobs = jobs.filter(job => {
    const matchesSearch = job.title.toLowerCase().includes(search.toLowerCase()) ||
      job.description?.toLowerCase().includes(search.toLowerCase());
    const matchesStatus = statusFilter === 'all' || job.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-4 border-[#1F3D2B] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="client-jobs">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-[#2E2E2E]" data-testid="client-jobs-title"><span className="gradient-text">{t('portalUi.client.jobs.title', 'Darbi')}</span></h1>
        <p className="text-[#2E2E2E]/60 mt-1" data-testid="client-jobs-subtitle">
          {t('portalUi.client.jobs.subtitlePrefix', 'Pārskatiet savus darbus un to')} <span className="text-[#1F3D2B] font-semibold">{t('portalUi.client.jobs.subtitleHighlight', 'statusus')}</span>
        </p>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#2E2E2E]/40" />
          <Input
            placeholder={t('portalUi.client.jobs.searchPlaceholder', 'Meklēt darbus...')} data-testid="client-jobs-search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-10"
          />
        </div>
        <div className="flex gap-2">
          <Button
            variant={statusFilter === 'all' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setStatusFilter('all')} data-testid="client-jobs-filter-all"
            className={statusFilter === 'all' ? 'bg-[#1F3D2B]' : ''}
          >
            {t('portalUi.client.jobs.filterAll', 'Visi')}
          </Button>
          <Button
            variant={statusFilter === 'planned' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setStatusFilter('planned')} data-testid="client-jobs-filter-planned"
            className={statusFilter === 'planned' ? 'bg-blue-600' : ''}
          >
            {t('portalUi.client.jobs.filterPlanned', 'Plānoti')}
          </Button>
          <Button
            variant={statusFilter === 'in_progress' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setStatusFilter('in_progress')} data-testid="client-jobs-filter-in-progress"
            className={statusFilter === 'in_progress' ? 'bg-amber-600' : ''}
          >
            {t('portalUi.client.jobs.filterInProgress', 'Procesā')}
          </Button>
          <Button
            variant={statusFilter === 'completed' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setStatusFilter('completed')} data-testid="client-jobs-filter-completed"
            className={statusFilter === 'completed' ? 'bg-green-600' : ''}
          >
            {t('portalUi.client.jobs.filterCompleted', 'Pabeigti')}
          </Button>
        </div>
      </div>

      {/* Jobs List */}
      {filteredJobs.length > 0 ? (
        <div className="grid gap-4">
          {filteredJobs.map((job) => {
            const StatusIcon = statusIcons[job.status];
            return (
              <Link key={job.id} to={`/portal/jobs/${job.id}`} data-testid={`client-job-card-${job.id}`}>
                <Card className="border-0 shadow-sm hover:shadow-xl hover:-translate-y-1 hover-lift card-shine transition-all cursor-pointer group">
                  <CardContent className="p-6">
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex items-start gap-4 flex-1">
                        <div className={`w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 ${
                          job.status === 'completed' ? 'bg-green-100' :
                          job.status === 'in_progress' ? 'bg-amber-100' : 'bg-blue-100'
                        }`}>
                          <StatusIcon className={`w-6 h-6 ${
                            job.status === 'completed' ? 'text-green-600' :
                            job.status === 'in_progress' ? 'text-amber-600' : 'text-blue-600'
                          }`} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-3 mb-1">
                            <h3 className="font-semibold text-[#2E2E2E] group-hover:text-[#1F3D2B] transition-colors">
                              {job.title}
                            </h3>
                            <span className={`px-3 py-1 rounded-full text-xs font-medium border ${statusColors[job.status]}`}>
                              {statusLabels[job.status]}
                            </span>
                          </div>
                          {job.description && (
                            <p className="text-sm text-[#2E2E2E]/60 line-clamp-2 mb-3">
                              {job.description}
                            </p>
                          )}
                          <div className="flex flex-wrap gap-4 text-sm text-[#2E2E2E]/60">
                            {job.responsible_person && (
                              <div className="flex items-center gap-1">
                                <User className="w-4 h-4" />
                                {job.responsible_person}
                              </div>
                            )}
                            {job.planned_end_date && (
                              <div className="flex items-center gap-1">
                                <Calendar className="w-4 h-4" />
                                {t('portalUi.client.jobs.deadlineLabel', 'Līdz')}: {new Date(job.planned_end_date).toLocaleDateString(locale)}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                      <ChevronRight className="w-5 h-5 text-[#2E2E2E]/40 group-hover:text-[#1F3D2B] group-hover:translate-x-1 transition-all flex-shrink-0" />
                    </div>
                  </CardContent>
                </Card>
              </Link>
            );
          })}
        </div>
      ) : (
        <Card className="border-0 shadow-sm card-shine" data-testid="client-jobs-empty">
          <CardContent className="p-12 text-center">
            <Briefcase className="w-16 h-16 mx-auto mb-4 text-[#2E2E2E]/20" />
            <h3 className="text-lg font-semibold text-[#2E2E2E] mb-2">{t('portalUi.client.jobs.emptyTitle', 'Nav atrasti darbi')}</h3>
            <p className="text-[#2E2E2E]/60">
              {search || statusFilter !== 'all'
                ? t('portalUi.client.jobs.emptyFiltered', 'Mēģiniet mainīt meklēšanas kritērijus')
                : t('portalUi.client.jobs.emptyAll', 'Vēl nav pievienoti darbi')}
            </p>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default JobsList;
